/**
 * What a workspace card shows: while it is loading, when it fails, and once it has a list.
 *
 * One mechanism, split three ways only by which state it renders — `useLoaded` holds the
 * state, `Loading` and `Failure` are the two it renders instead of content. Keeping them
 * together is the point: a card that imported the hook from one module and its two
 * placeholders from another would let the states drift apart, and "loading" with nothing on
 * screen is the failure nobody reports.
 *
 * The paging constants live here for the same reason. They describe how much a card asks
 * for and how long it waits before asking again, which is a property of showing a list
 * rather than of the record being shown.
 */

import { useCallback, useEffect, useState, type JSX } from 'react'

import type { FindingSummary } from '../account'
import { describeFailure } from '@shared/user-errors.js'
import { t } from '../i18n'

export const PAGE_SIZE = 50

/** How long to wait after a keystroke before asking the server again. */
export const FILTER_DEBOUNCE_MS = 250

/** What a load is doing right now: still going, done, or failed with a message. */
export type LoadState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

/**
 * Load something, with an explicit way to load it again.
 *
 * Editing anything in the workspace changes what a list should show, and a
 * hook that could only load once would force every list to keep a shadow copy
 * of the server's state and patch it by hand — which is how a list comes to
 * disagree with the thing it is listing.
 */
export function useLoaded<T>(
  load: () => Promise<T>,
  key: string,
): { state: LoadState<T>; reload: () => void } {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    load()
      .then((data) => !cancelled && setState({ status: 'ready', data }))
      .catch(
        (error: unknown) =>
          !cancelled && setState({ status: 'error', message: describeFailure(error, t('error.notLoaded')) }),
      )
    return () => {
      cancelled = true
    }
    // `load` is a fresh closure every render; the key identifies the resource.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce])

  return { state, reload: useCallback(() => setNonce((value) => value + 1), []) }
}

export function Loading(): JSX.Element {
  return <p className="muted">…</p>
}

export function Failure({ message }: { message: string }): JSX.Element {
  return (
    <p className="failure" role="alert">
      {message}
    </p>
  )
}

/**
 * What a finding's checks add up to, in one line a reader can ignore.
 *
 * "Unverified" is never counted as a problem: it means we hold no licence to
 * the translation the wording was attributed to, which is a fact about this
 * corpus rather than a defect in the finding.
 */
export function citationLine(finding: FindingSummary): string {
  const { total, outstanding, unverified } = finding.citations
  if (total === 0) return ''
  if (outstanding > 0) return t('citations.needsReview', { count: outstanding })
  if (unverified > 0) return t('citations.someUnverified', { count: unverified })
  return t('citations.allResolve', { count: total })
}
